/**
 * Zugriff auf die API.
 *
 * Die Sitzung steckt in einem HttpOnly-Cookie, das JavaScript nicht lesen
 * kann. Für schreibende Anfragen verlangt der Server den Wert des
 * CSRF-Cookies zusätzlich im Kopf X-CSRF-Token – dieses Cookie ist
 * absichtlich lesbar. Ein fremder Ursprung schickt das Cookie zwar mit, kann
 * es aber nicht lesen und den Kopf daher nicht setzen.
 */

import { ApiFehler, fehlerAusAntwort } from './fehler'

const SCHREIBENDE_METHODEN = new Set(['POST', 'PUT', 'PATCH', 'DELETE'])

function csrfAusCookie(): string | undefined {
  const treffer = document.cookie
    .split(';')
    .map((teil) => teil.trim())
    .find((teil) => teil.startsWith('spuer_csrf='))
  return treffer ? decodeURIComponent(treffer.slice('spuer_csrf='.length)) : undefined
}

type Optionen = {
  methode?: string
  koerper?: unknown
  formular?: FormData
  signal?: AbortSignal
}

async function anfragen<T>(pfad: string, optionen: Optionen = {}): Promise<T> {
  const methode = optionen.methode ?? 'GET'
  const kopf: Record<string, string> = {}

  if (SCHREIBENDE_METHODEN.has(methode)) {
    const csrf = csrfAusCookie()
    if (csrf) kopf['X-CSRF-Token'] = csrf
  }

  let koerper: BodyInit | undefined
  if (optionen.formular) {
    // Bei FormData setzt der Browser den Content-Type samt boundary selbst.
    koerper = optionen.formular
  } else if (optionen.koerper !== undefined) {
    kopf['Content-Type'] = 'application/json'
    koerper = JSON.stringify(optionen.koerper)
  }

  const antwort = await fetch(pfad, {
    method: methode,
    headers: kopf,
    body: koerper,
    // Cookies mitschicken, auch wenn die Oberfläche später unter einer
    // anderen Adresse als die API läuft.
    credentials: 'same-origin',
    signal: optionen.signal,
  })

  if (antwort.status === 204) return undefined as T

  const text = await antwort.text()
  let inhalt: unknown = undefined
  if (text) {
    try {
      inhalt = JSON.parse(text)
    } catch {
      // Kein JSON – etwa eine HTML-Fehlerseite vom Proxy davor.
      inhalt = text
    }
  }

  if (!antwort.ok) throw fehlerAusAntwort(antwort.status, inhalt)

  return inhalt as T
}

export const api = {
  holen: <T>(pfad: string, signal?: AbortSignal) => anfragen<T>(pfad, { signal }),

  senden: <T>(pfad: string, koerper?: unknown) =>
    anfragen<T>(pfad, { methode: 'POST', koerper }),

  setzen: <T>(pfad: string, koerper?: unknown) =>
    anfragen<T>(pfad, { methode: 'PUT', koerper }),

  aendern: <T>(pfad: string, koerper?: unknown) =>
    anfragen<T>(pfad, { methode: 'PATCH', koerper }),

  loeschen: (pfad: string) => anfragen<void>(pfad, { methode: 'DELETE' }),

  // Löschen mit Inhalt: die Rückfrage beim Kundenlöschen reist im Körper
  // mit, weil sie nicht in eine Adresse gehört – dort stünde sie in jedem
  // Protokoll.
  loeschenMit: (pfad: string, koerper: unknown) =>
    anfragen<void>(pfad, { methode: 'DELETE', koerper }),

  hochladen: <T>(pfad: string, formular: FormData) =>
    anfragen<T>(pfad, { methode: 'POST', formular }),
}

export { ApiFehler }
